import { useCallback } from 'react';

import { SWING_POSITIONS } from '@core';
import { SWING_FOUNDATIONS_KIOSK, useBatchUpdateParameters, useUpdateActivity } from '@queries';
import { activityAtom, layoutCustomizationsAtom, P_POSITION_REGEX, parametersMapAtom } from '@store';
import { useAtomValue } from 'jotai';
import { toast } from 'sonner';

const getFloorSFModuleId = (position: string) => `swing_foundations.default_floor_${position}`;

/**
 * Saves Swing Foundation parameters for the active layout and its activity.
 * Call within the active activity's Jotai scope, after layout customizations and parameter nodes are loaded.
 */
export const useSaveSwingFoundationParameterSelection = () => {
    const customizations = useAtomValue(layoutCustomizationsAtom);
    const activity = useAtomValue(activityAtom);
    const parameterMap = useAtomValue(parametersMapAtom);
    const { mutateAsync: batchUpdateParameters } = useBatchUpdateParameters();
    const { mutateAsync: updateActivity } = useUpdateActivity();

    return useCallback(
        async (parameterIds: string[]) => {
            if (!activity?.layoutId || !customizations) {
                return;
            }

            const parameterIdsByPosition = parameterIds.reduce<Record<string, string[]>>((acc, id) => {
                const parameter = parameterMap[id];
                const position =
                    parameter?.parameter.position ??
                    parameter?.categories.swingFoundationGroup?.match(P_POSITION_REGEX)?.[0];

                if (!position) {
                    return acc;
                }

                if (!acc[position]) {
                    acc[position] = [];
                }

                acc[position].push(id);
                return acc;
            }, {});

            const updates = [
                {
                    moduleId: SWING_FOUNDATIONS_KIOSK,
                    showChildOn: 'kiosk' as const,
                    parameterIds,
                },
                ...SWING_POSITIONS.map((position) => ({
                    moduleId: getFloorSFModuleId(position),
                    showChildOn: 'floor' as const,
                    parameterIds: parameterIdsByPosition[position] ?? [],
                })),
            ];

            const updatedLayout = await batchUpdateParameters({ layoutId: activity.layoutId, updates });

            if (!updatedLayout) {
                toast('Failed to update layout');
                console.warn('[ERROR UPDATING LAYOUT]', updatedLayout);
                return;
            }

            await updateActivity({ layoutId: updatedLayout.id, activityId: activity.id });
        },
        [activity?.layoutId, activity, batchUpdateParameters, customizations, parameterMap, updateActivity],
    );
};
